import { Group, Text } from '@mantine/core';
import { HumanName, Practitioner } from '@medplum/fhirtypes';
import { ReactElement } from 'react';
import { formatGivenFamilyName, getInitialsFromHumanName } from '@bariendo/lib/human-name';
import { ActorAvatar } from '@/components/Timeline/TimelineItems/ActorAvatar';
import { TeammateAvatar } from '@/components/TeamAvatar';

/**
 * A people-column cell: who, next to when.
 *
 * A date alone tells a rep the thread is warm but not whether it is theirs. The
 * avatar makes the row answer both, and lets a rep scan their own initials down
 * a shared queue without a filter.
 *
 * Single line, avatar leading — a 42px row has no space for a stacked cell.
 * `text` arrives **already formatted** so each column keeps its own date policy:
 * consult, intro call and last communication carry the year, task dates
 * deliberately do not.
 *
 * An automated send takes the system marker rather than a person's initials —
 * the same split the timeline makes between teammate-written and webhook-written
 * cards. A drip is not a colleague having reached out, and rows would read as
 * covered if it were. This reuses `ActorAvatar`'s existing system variant rather
 * than introducing a second system marker in Orbit.
 */
export interface PersonCellProps {
  /** Pre-formatted display text. Empty renders an em-dash, never a blank cell. */
  text: string;
  /** The person this row's column refers to, when it is a teammate. */
  name?: HumanName;
  /** Set for an automated send. Wins over `name`. Doubles as the tooltip. */
  systemLabel?: string;
  /**
   * Amber the text. Colour stays at two across these queues — an overdue task
   * date, a failed warm transfer, a no-show — so that the exceptions are what
   * the eye lands on. Everything else is neutral.
   */
  emphasis?: 'warning';
}

export function PersonCell({ text, name, systemLabel, emphasis }: PersonCellProps): ReactElement {
  // Decides *whether* a teammate avatar appears at all. `TeammateAvatar` then
  // derives the glyph from the display string with its own rule; for a single
  // given name plus a family name, which is every provider name in practice,
  // the two rules agree.
  const initials = getInitialsFromHumanName(name);
  const displayName = formatGivenFamilyName(name);

  return (
    <Group gap="xs" wrap="nowrap">
      {systemLabel ? (
        <ActorAvatar variant="system" label={systemLabel} />
      ) : initials ? (
        <TeammateAvatar name={displayName} />
      ) : null}
      <Text component="span" c={emphasis === 'warning' ? 'orange.7' : undefined}>
        {text || '—'}
      </Text>
    </Group>
  );
}

/**
 * Several teammates in one cell — the Patient navigator column.
 *
 * One avatar per person, names joined after them, on one line: the same 42px row
 * as `PersonCell`, and the avatars are what let a navigator find their own
 * patients by scanning the column. Empty renders an em-dash, never a blank cell.
 *
 * Given `practitioners`, the avatars show photos and name the person on hover;
 * the Intake hub's PN column passes them with `withNames={false}`, avatars only,
 * two then `+N`.
 */
export interface PeopleCellProps {
  names: HumanName[];
  practitioners?: Practitioner[];
  withNames?: boolean;
}

const MAX_AVATARS_WITHOUT_NAMES = 2;

export function PeopleCell({ names, practitioners, withNames = true }: PeopleCellProps): ReactElement {
  if (practitioners) {
    return <PractitionerAvatars practitioners={practitioners} withNames={withNames} />;
  }

  const people = names.filter((name) => getInitialsFromHumanName(name));

  if (people.length === 0) {
    return <Text component="span">—</Text>;
  }

  return (
    <Group gap="xs" wrap="nowrap">
      <Group gap={2} wrap="nowrap">
        {people.map((name, index) => (
          <TeammateAvatar key={`${formatGivenFamilyName(name)}-${index}`} name={formatGivenFamilyName(name)} />
        ))}
      </Group>
      {withNames && <Text component="span">{people.map(formatGivenFamilyName).join(', ')}</Text>}
    </Group>
  );
}

function PractitionerAvatars({
  practitioners,
  withNames,
}: {
  practitioners: Practitioner[];
  withNames: boolean;
}): ReactElement {
  if (practitioners.length === 0) {
    return <Text component="span">—</Text>;
  }

  const shown = withNames ? practitioners : practitioners.slice(0, MAX_AVATARS_WITHOUT_NAMES);
  const hidden = practitioners.length - shown.length;

  return (
    <Group gap="xs" wrap="nowrap">
      <Group gap={2} wrap="nowrap">
        {shown.map((practitioner, index) => (
          <TeammateAvatar
            key={practitioner.id ?? index}
            practitioner={practitioner}
            label={formatGivenFamilyName(practitioner.name?.[0]) || undefined}
          />
        ))}
        {hidden > 0 && (
          <Text component="span" fz={12} c="dimmed">
            +{hidden}
          </Text>
        )}
      </Group>
      {withNames && (
        <Text component="span">
          {practitioners.map((practitioner) => formatGivenFamilyName(practitioner.name?.[0])).join(', ')}
        </Text>
      )}
    </Group>
  );
}
